import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, Loading } from '../components/ui';
import { useCrud } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface SocialLink {
  id: number;
  name: string;
  url: string;
  icon: string;
  sort_order: number;
}

const SocialLinksPage: React.FC = () => {
  const socialLinks = useCrud<SocialLink>('/api/social-links');
  const { showToast } = useToast();
  const [editingLink, setEditingLink] = useState<SocialLink | null>(null);

  useEffect(() => { socialLinks.fetchAll(); }, []);

  const handleSave = async (link: Partial<SocialLink>) => {
    if (editingLink?.id) {
      const result = await socialLinks.update(editingLink.id, link);
      if (result) {
        showToast('Social link updated!', 'success');
        setEditingLink(null);
      }
    } else {
      const result = await socialLinks.create(link);
      if (result) {
        showToast('Social link added!', 'success');
        setEditingLink(null);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this social link?')) {
      const result = await socialLinks.remove(id);
      if (result) showToast('Social link deleted', 'success');
    }
  };

  if (socialLinks.loading && socialLinks.items.length === 0) return <Loading />;

  return (
    <>
      <PageHeader
        title="Social Links"
        subtitle="Manage your social media links shown in the Contact section"
        actions={
          <Button onClick={() => setEditingLink({ id: 0, name: '', url: '', icon: '🔗', sort_order: socialLinks.items.length })}>
            + Add Link
          </Button>
        }
      />

      <div className="admin-content">
        <Card>
          <div className="admin-list-editor">
            {socialLinks.items.map(link => (
              <div key={link.id} className="admin-list-item">
                <span style={{ fontSize: '1.5rem' }}>{link.icon}</span>
                <div className="admin-list-item-content">
                  <div>
                    <strong>{link.name}</strong>
                    <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {link.url}
                    </div>
                  </div>
                </div>
                <div className="admin-list-item-actions">
                  <Button size="sm" variant="secondary" onClick={() => setEditingLink(link)}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(link.id)}>Delete</Button>
                </div>
              </div>
            ))}
            {socialLinks.items.length === 0 && (
              <p style={{ color: 'var(--admin-text-muted)', textAlign: 'center', padding: 20 }}>
                No social links yet. Add your first link above.
              </p>
            )}
          </div>
        </Card>

        {editingLink && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }} onClick={() => setEditingLink(null)}>
            <div style={{ background: 'var(--admin-surface)', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500 }} onClick={e => e.stopPropagation()}>
              <h3 style={{ marginBottom: 20 }}>{editingLink.id ? 'Edit Social Link' : 'Add Social Link'}</h3>
              <SocialLinkForm
                link={editingLink}
                onSave={handleSave}
                onCancel={() => setEditingLink(null)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const SocialLinkForm: React.FC<{
  link: SocialLink;
  onSave: (l: Partial<SocialLink>) => void;
  onCancel: () => void;
}> = ({ link, onSave, onCancel }) => {
  const [form, setForm] = useState(link);

  return (
    <div className="admin-form">
      <Input label="Platform Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Spotify" required />
      <Input label="Icon (emoji)" value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="🎵" />
      <Input label="URL" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://..." required />
      <Input label="Sort Order" type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />

      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <Button onClick={() => onSave(form)}>Save</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default SocialLinksPage;
